import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Line, Radar } from 'react-chartjs-2';
import { Developer, DeveloperIterationData, Project } from '../types/DeveloperTypes';
import { generateAiSummary } from '../api';
import IconButton from './AIAnalysisButton';
import DeveloperBurndownChart from './DeveloperBurndownChart';
import { formatMarkdown } from '../utils/markdownFormatter';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  RadialLinearScale,
  Filler,
  Title,
  Tooltip,
  Legend,
  BarElement,
} from 'chart.js';
import { format } from 'date-fns';
import MobileGlassSlider from './MobileGlassSlider';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  RadialLinearScale,
  Filler,
  Title,
  Tooltip,
  Legend,
  BarElement
);

interface OverEstimateItem {
  id: string;
  title: string;
  originalEstimate: number;
  actualDuration: number;
  overEstimateRatio: number;
}

interface DeveloperDetailCardProps {
  developer: Developer | null;
  onClose: () => void;
  projectId?: number;
  teamData?: {
    recentIterations: any[];
    developers: any[];
    metrics: {
      openBugs: {
        value: number;
        trend: number;
      };
      avgPRReviewTime: {
        value: number;
        trend: number;
      };
      completedStories: {
        value: number;
        trend: number;
      };
      sprintGoalAchievement: {
        value: number;
        trend: number;
      };
      velocityPercentage: {
        value: number;
        trend: number;
      };
    };
  };
}

// Lightweight viewport hook for responsive behavior
function useViewport() {
  const [width, setWidth] = useState<number>(typeof window !== 'undefined' ? window.innerWidth : 1920);
  useEffect(() => {
    const onResize = () => setWidth(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  const isMobile = width <= 768;
  const isTablet = width > 768 && width <= 1024;
  return { width, isMobile, isTablet };
}

// Developer Detail Card Component
const DeveloperDetailCard: React.FC<DeveloperDetailCardProps> = ({ developer, onClose, projectId, teamData }) => {
  const { isMobile, isTablet } = useViewport();
  const glassCardBase = {
    background: 'rgba(255, 255, 255, 0.55)',
    borderRadius: '16px',
    padding: isMobile ? '16px' : '24px',
    boxShadow: '0 10px 30px rgba(0,0,0,0.08), 0 1px 0 rgba(255,255,255,0.6) inset',
    border: '1px solid rgba(255,255,255,0.35)',
    backdropFilter: 'blur(12px)',
    WebkitBackdropFilter: 'blur(12px)',
    position: 'relative' as const
  };
  const [generatingAI, setGeneratingAI] = useState<Record<string, boolean>>({});
  const [iterationAISummaries, setIterationAISummaries] = useState<Record<string, string>>({});
  const [burndownAnalysis, setBurndownAnalysis] = useState<string>('');
  const [generatingBurndownAnalysis, setGeneratingBurndownAnalysis] = useState<boolean>(false);
  const [burndownAnalysisStream, setBurndownAnalysisStream] = useState<ReadableStreamDefaultReader | null>(null);
  const [currentStreams, setCurrentStreams] = useState<Record<string, ReadableStreamDefaultReader | null>>({});
  const [modalContent, setModalContent] = useState<{
    isOpen: boolean;
    title: string;
    content: string;
    type?: 'iteration' | 'burndown';
    iterationId?: string;
    developerId?: string
  }>({
    isOpen: false,
    title: '',
    content: '',
    type: undefined,
    iterationId: undefined,
    developerId: undefined
  });

  // Team overview: Radar developer selection state
  const [isDevSelectModalOpen, setIsDevSelectModalOpen] = useState<boolean>(false);
  const [selectedRadarDeveloperIds, setSelectedRadarDeveloperIds] = useState<string[]>([]);
  const [radarSortBy, setRadarSortBy] = useState<'name' | 'over' | 'under' | 'selected'>('over');
  const [radarSortDir, setRadarSortDir] = useState<'asc' | 'desc'>('desc');

  // Helper to compute totals and percentages for a developer
  const computeEstimateMetrics = useCallback((dev: any) => {
    const totals = dev.IterationData?.reduce((acc: { work: number; over: number; under: number }, iter: DeveloperIterationData) => {
      return {
        work: acc.work + (iter.WorkItemTasksAssigned || 0),
        over: acc.over + (iter.ItemsOverEstimate || 0),
        under: acc.under + (iter.ItemsUnderEstimate || 0),
      };
    }, { work: 0, over: 0, under: 0 }) || { work: 0, over: 0, under: 0 };
    const overPct = totals.work > 0 ? (totals.over / totals.work) * 100 : 0;
    const underPct = totals.work > 0 ? (totals.under / totals.work) * 100 : 0;
    return { totalWorkItems: totals.work, overPct, underPct };
  }, []);

  // Default selection: top 5 over-estimate and top 5 under-estimate (union)
  useEffect(() => {
    if (!teamData?.developers) return;
    const withWork = teamData.developers.filter((d: any) => {
      const m = computeEstimateMetrics(d);
      return m.totalWorkItems > 0;
    });
    const topOver = [...withWork]
      .sort((a: any, b: any) => computeEstimateMetrics(b).overPct - computeEstimateMetrics(a).overPct)
      .slice(0, 5)
      .map((d: any) => d.DeveloperId?.toString());
    const topUnder = [...withWork]
      .sort((a: any, b: any) => computeEstimateMetrics(b).underPct - computeEstimateMetrics(a).underPct)
      .slice(0, 5)
      .map((d: any) => d.DeveloperId?.toString());
    const union = Array.from(new Set([...topOver,...topUnder].filter(Boolean)));
    setSelectedRadarDeveloperIds(union);
  }, [teamData?.developers, computeEstimateMetrics]);

  // Materialize developer metrics for team overview
  const devsWithMetrics = useMemo(() => {
    if (!teamData?.developers) return [] as Array<any>;
    return teamData.developers.map((d: any) => {
      const m = computeEstimateMetrics(d);
      return { ...d, _metrics: m, _id: d.DeveloperId?.toString() };
    });
  }, [teamData?.developers, computeEstimateMetrics]);

  const selectedRadarDevs = useMemo(() => {
    if (!devsWithMetrics.length) return [] as Array<any>;
    const ids = new Set(selectedRadarDeveloperIds);
    return devsWithMetrics.filter(d => d._metrics.totalWorkItems > 0 && (ids.size === 0 || ids.has(d._id)));
  }, [devsWithMetrics, selectedRadarDeveloperIds]);

  // Sorted list for modal display
  const sortedDevsForModal = useMemo(() => {
    const base = devsWithMetrics.filter((d: any) => d._metrics.totalWorkItems > 0);
    const selectedSet = new Set(selectedRadarDeveloperIds);
    const cmp = (a: any, b: any) => {
      let av: number | string = 0;
      let bv: number | string = 0;
      switch (radarSortBy) {
        case 'name':
          av = a.DeveloperName || '';
          bv = b.DeveloperName || '';
          return radarSortDir === 'asc' ? (av as string).localeCompare(bv as string) : (bv as string).localeCompare(av as string);
        case 'over':
          av = Number.isFinite(a._metrics?.overPct) ? a._metrics.overPct : -Infinity;
          bv = Number.isFinite(b._metrics?.overPct) ? b._metrics.overPct : -Infinity;
          return radarSortDir === 'asc' ? (av as number) - (bv as number) : (bv as number) - (av as number);
        case 'under':
          av = Number.isFinite(a._metrics?.underPct) ? a._metrics.underPct : -Infinity;
          bv = Number.isFinite(b._metrics?.underPct) ? b._metrics.underPct : -Infinity;
          return radarSortDir === 'asc' ? (av as number) - (bv as number) : (bv as number) - (av as number);
        case 'selected':
          av = selectedSet.has(a._id) ? 1 : 0;
          bv = selectedSet.has(b._id) ? 1 : 0;
          return radarSortDir === 'asc' ? (av as number) - (bv as number) : (bv as number) - (av as number);
        default:
          return 0;
      }
    };
    return [...base].sort(cmp);
  }, [devsWithMetrics, radarSortBy, radarSortDir, selectedRadarDeveloperIds]);

  const toggleSort = useCallback((by: 'name' | 'over' | 'under' | 'selected') => {
    if (radarSortBy === by) {
      setRadarSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setRadarSortBy(by);
      setRadarSortDir('desc');
    }
  }, [radarSortBy]);

  // Material Icons font is now loaded by the IconButton component

  // Cleanup streams when developer changes or component unmounts
  useEffect(() => {
    return () => {
      // Cancel all ongoing streams
      Object.values(currentStreams).forEach(async (stream) => {
        if (stream) {
          try {
            await stream.cancel();
          } catch (error) {
            console.error('Error canceling stream:', error);
          }
        }
      });

      // Cancel burndown analysis stream
      if (burndownAnalysisStream) {
        try {
          burndownAnalysisStream.cancel();
        } catch (error) {
          console.error('Error canceling burndown analysis stream:', error);
        }
      }
    };
  }, [developer?.DeveloperId]);

  // Cancel streams when developer changes
  useEffect(() => {
    // Cancel all ongoing streams when switching developers
    Object.entries(currentStreams).forEach(async ([key, stream]) => {
      if (stream) {
        try {
          await stream.cancel();
          setCurrentStreams(prev => ({ ...prev, [key]: null }));
          setGeneratingAI(prev => ({ ...prev, [key]: false }));
        } catch (error) {
          console.error('Error canceling stream:', error);
        }
      }
    });
  }, [developer?.DeveloperId]);

  // Initialize AI summaries from existing data when developer loads
  useEffect(() => {
    if (developer?.IterationData) {
      const initialSummaries: Record<string, string> = {};
      developer.IterationData.forEach(iteration => {
        if (iteration.AiSummary) {
          const summaryKey = `${developer.DeveloperId}-${iteration.IterationId}`;
          initialSummaries[summaryKey] = iteration.AiSummary.summary;
        }
      });
      setIterationAISummaries(initialSummaries);
    }
  }, [developer?.DeveloperId, developer?.IterationData]);

  // Initialize burndown analysis from existing data when developer loads
  useEffect(() => {
    if (developer?.BurndownDataAISummary) {
      setBurndownAnalysis(developer.BurndownDataAISummary.summary);
    } else {
      setBurndownAnalysis('');
    }
  }, [developer?.DeveloperId, developer?.BurndownDataAISummary]);

  const handleGenerateIterationSummary = async (iterationId: string, developerId: string) => {
    if (!projectId || !iterationId || !developerId) return;

    const summaryKey = `${developerId}-${iterationId}`;

    // Interrupt any ongoing stream for this iteration
    const existingStream = currentStreams[summaryKey];
    if (existingStream) {
      await existingStream.cancel();
      setCurrentStreams(prev => ({ ...prev, [summaryKey]: null }));
    }

    try {
      setGeneratingAI(prev => ({ ...prev, [summaryKey]: true }));

      // Clear existing content
      setIterationAISummaries(prev => ({ ...prev, [summaryKey]: '' }));

      // If modal is open for this iteration, clear its content too
      if (modalContent.isOpen && modalContent.iterationId === iterationId && modalContent.developerId === developerId) {
        setModalContent(prev => ({ ...prev, content: '' }));
      }

      const stream = await generateAiSummary('developer-iteration', `${developerId}/${iterationId}`, projectId.toString());
      setCurrentStreams(prev => ({ ...prev, [summaryKey]: stream.stream }));

      for await (const chunk of stream) {
        setIterationAISummaries(prev => ({
          ...prev,
          [summaryKey]: (prev[summaryKey] || '') + chunk
        }));

        // Also update modal content if it's open for this iteration
        if (modalContent.isOpen && modalContent.iterationId === iterationId && modalContent.developerId === developerId) {
          setModalContent(prev => ({
            ...prev,
            content: (prev.content || '') + chunk
          }));
        }
      }
    } catch (error) {
      console.error('Error generating iteration AI summary:', error);
      const errorMessage = 'Error generating AI summary. Please try again.';
      setIterationAISummaries(prev => ({
        ...prev,
        [summaryKey]: errorMessage
      }));

      // Also update modal content if it's open for this iteration
      if (modalContent.isOpen && modalContent.iterationId === iterationId && modalContent.developerId === developerId) {
        setModalContent(prev => ({ ...prev, content: errorMessage }));
      }
    } finally {
      setGeneratingAI(prev => ({ ...prev, [summaryKey]: false }));
      setCurrentStreams(prev => ({ ...prev, [summaryKey]: null }));
    }
  };

  const handleGenerateBurndownAnalysis = async (developerId: string) => {
    if (!projectId || !developerId) return;

    // Interrupt any ongoing stream
    if (burndownAnalysisStream) {
      await burndownAnalysisStream.cancel();
      setBurndownAnalysisStream(null);
    }

    try {
      setGeneratingBurndownAnalysis(true);
      setBurndownAnalysis('');

      // If modal is open for burndown, clear its content too
      if (modalContent.isOpen && modalContent.type === 'burndown' && modalContent.developerId === developerId) {
        setModalContent(prev => ({ ...prev, content: '' }));
      }

      const stream = await generateAiSummary('developer-burndowns', developerId, projectId.toString());
      setBurndownAnalysisStream(stream.stream);

      let accumulatedContent = '';
      const reader = stream.stream;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = new TextDecoder().decode(value);
        const nextContent = accumulatedContent + chunk;
        accumulatedContent = nextContent;
        setBurndownAnalysis(nextContent);

        // Update modal content if it's open for this burndown
        if (modalContent.isOpen && modalContent.type === 'burndown' && modalContent.developerId === developerId) {
          setModalContent({ ...modalContent, content: nextContent });
        }
      }
    } catch (error) {
      console.error('Error generating burndown analysis:', error);
      setBurndownAnalysis('Error generating analysis. Please try again.');
    } finally {
      setGeneratingBurndownAnalysis(false);
      setBurndownAnalysisStream(null);
    }
  };

  const handleOpenModal = (title: string, content: string, type?: 'iteration' | 'burndown', iterationId?: string, developerId?: string) => {
    setModalContent({
      isOpen: true,
      title,
      content,
      type,
      iterationId,
      developerId
    });
  };

  const handleCloseModal = () => {
    setModalContent({
      isOpen: false,
      title: '',
      content: '',
      type: undefined,
      iterationId: undefined,
      developerId: undefined
    });
  };

  //team performance overview - no developer selected
  if (!developer && teamData) {
    // Team Overview Mode
    return (
      <div style={glassCardBase}>
        {/* <button 
          onClick={onClose}
          style={{
            position: 'absolute',
            top: '12px',
            right: '12px',
            background: 'none',
            border: 'none',
            fontSize: '20px',
            cursor: 'pointer'
          }}
        >
          ×
        </button> */}

        <h2>Team Performance Overview</h2>

        {/* Quick Stats Section */}
        <div className="quick-stats-grid" style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? 'repeat(3, 1fr)' : (isTablet ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)'),
          gap: isMobile ? '12px' : '16px',
          marginBottom: isMobile ? '16px' : '24px'
        }}>
          <div style={{
            background: 'rgba(255,255,255,0.6)',
            borderRadius: '12px',
            padding: isMobile ? '12px' : '16px',
            textAlign: 'center',
            boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
          }}>
            <div style={{ fontSize: '14px', color: '#666', marginBottom: '8px', minHeight: '36px' }}>Sprint Attainment</div>
            <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#3949ab' }}>
              {teamData.metrics.sprintGoalAchievement.value.toFixed(0)}%
            </div>
          </div>
          <div style={{
            background: 'rgba(255,255,255,0.6)',
            borderRadius: '12px',
            padding: isMobile ? '12px' : '16px',
            textAlign: 'center',
            boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
          }}>
            <div style={{ fontSize: '14px', color: '#666', marginBottom: '8px', minHeight: '36px' }}>Average PR Review Time</div>
            <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#3949ab' }}>
              {teamData.metrics.avgPRReviewTime.value.toFixed(1)} days
            </div>
          </div>
          <div style={{
            background: 'rgba(255,255,255,0.6)',
            borderRadius: '12px',
            padding: isMobile ? '12px' : '16px',
            textAlign: 'center',
            boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
          }}>
            <div style={{ fontSize: '14px', color: '#666', marginBottom: '8px', minHeight: '36px' }}>Open Bugs</div>
            <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#3949ab' }}>
              {teamData.metrics.openBugs.value}
            </div>
          </div>
          <div style={{
            background: 'rgba(255,255,255,0.6)',
            borderRadius: '12px',
            padding: isMobile ? '12px' : '16px',
            textAlign: 'center',
            boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
          }}>
            <div style={{ fontSize: '14px', color: '#666', marginBottom: '8px', minHeight: '36px' }}>Velocity Attainment</div>
            <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#3949ab' }}>
              {teamData.metrics.velocityPercentage.value.toFixed(0)}%
            </div>
          </div>
        </div>

        {/* Team Performance Trends */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
          gap: isMobile ? '16px' : '24px',
          marginBottom: isMobile ? '16px' : '24px'
        }}>

          {/* Estimate Accuracy Distribution */}
          <div style={{
            background: 'rgba(255,255,255,0.6)',
            borderRadius: '12px',
            padding: isMobile ? '12px' : '16px',
            gridRow: 'span 2',
            boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
          }}>
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center', 
              gap: '8px', 
              marginBottom: '8px' }}>
              <h3 style={{
                fontSize: '16px',
                margin: 0,
                color: '#3949ab'
              }}>Estimate Accuracy Distribution</h3>
              <button
                onClick={() => setIsDevSelectModalOpen(true)}
                style={{
                  padding: '6px 10px',
                  fontSize: '12px',
                  borderRadius: '8px',
                  background: '#3949ab',
                  color: 'white',
                  border: 'none',
                  cursor: 'pointer'
                }}
                title="Select developers to display"
              >
                Select developers
              </button>
            </div>
            <div style={{ height: isMobile ? '300px' : '500px' }}>
              <Radar
                data={{
                  labels: selectedRadarDevs.map(dev => dev.DeveloperName),
                  datasets: [
                    {
                      label: 'Delivered Over Estimate %',
                      data: selectedRadarDevs.map(dev => dev._metrics.overPct),
                      backgroundColor: 'rgba(244, 67, 54, 0.2)',
                      borderColor: 'rgb(244, 67, 54)',
                      pointBackgroundColor: 'rgb(244, 67, 54)',
                      pointBorderColor: '#fff',
                      pointHoverBackgroundColor: '#fff',
                      pointHoverBorderColor: 'rgb(244, 67, 54)'
                    },
                    {
                      label: 'Delivered Under Estimate %',
                      data: selectedRadarDevs.map(dev => dev._metrics.underPct),
                      backgroundColor: 'rgba(76, 175, 80, 0.2)',
                      borderColor: 'rgb(76, 175, 80)',
                      pointBackgroundColor: 'rgb(76, 175, 80)',
                      pointBorderColor: '#fff',
                      pointHoverBackgroundColor: '#fff',
                      pointHoverBorderColor: 'rgb(76, 175, 80)'
                    }
                  ]
                }}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  scales: {
                    r: {
                      beginAtZero: true,
                      max: 100,
                      ticks: {
                        stepSize: 20
                      }
                    }
                  },
                  plugins: {
                    tooltip: {
                      callbacks: {
                        label: function (context) {
                          return `${context.dataset.label}: ${(context.raw as number).toFixed(1)}%`;
                        }
                      }
                    }
                  }
                }}
              />
            </div>

            {isDevSelectModalOpen && createPortal(
              <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1000 }} onClick={() => setIsDevSelectModalOpen(false)}>
                <div
                  onClick={(e) => e.stopPropagation()}
                  style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: 'min(800px, 90vw)',
                    maxHeight: '80vh',
                    overflow: 'auto',
                    background: 'white',
                    borderRadius: '12px',
                    boxShadow: '0 8px 24px rgba(0,0,0,0.2)'
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', borderBottom: '1px solid #eee' }}>
                    <h3 style={{ margin: 0, fontSize: '16px', color: '#3949ab' }}>Select Developers for Radar</h3>
                    <button onClick={() => setIsDevSelectModalOpen(false)} style={{ background: 'none', border: 'none', fontSize: '18px', cursor: 'pointer' }} title="Close">×</button>
                  </div>
                  <div style={{ padding: '12px 16px' }}>
                    <div style={{ display: 'flex', gap: '8px', marginBottom: '12px', flexWrap: 'wrap' }}>
                      <button
                        onClick={() => setSelectedRadarDeveloperIds(devsWithMetrics.filter(d => d._metrics.totalWorkItems > 0).map((d: any) => d._id))}
                        style={{ padding: '6px 10px', borderRadius: '8px', border: '1px solid #ddd', background: '#f8f9fa', cursor: 'pointer' }}
                      >
                        Select all
                      </button>
                      <button
                        onClick={() => setSelectedRadarDeveloperIds([])}
                        style={{ padding: '6px 10px', borderRadius: '8px', border: '1px solid #ddd', background: '#f8f9fa', cursor: 'pointer' }}
                      >
                        Clear all
                      </button>
                      <button
                        onClick={() => {
                          const withWork = devsWithMetrics.filter((d: any) => d._metrics.totalWorkItems > 0);
                          const topOver = [...withWork].sort((a: any, b: any) => b._metrics.overPct - a._metrics.overPct).slice(0, 5).map((d: any) => d._id);
                          const topUnder = [...withWork].sort((a: any, b: any) => b._metrics.underPct - a._metrics.underPct).slice(0, 5).map((d: any) => d._id);
                          setSelectedRadarDeveloperIds(Array.from(new Set([...topOver, ...topUnder])));
                        }}
                        style={{ padding: '6px 10px', borderRadius: '8px', border: '1px solid #ddd', background: '#f8f9fa', cursor: 'pointer' }}
                      >
                        Top 5 Over + Under
                      </button>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr', gap: '8px', alignItems: 'center', fontSize: '13px', color: '#333' }}>
                      <div style={{ fontWeight: 600, cursor: 'pointer', userSelect: 'none' }} onClick={() => toggleSort('name')}>
                        Developer{radarSortBy === 'name' ? (radarSortDir === 'asc' ? ' ▲' : ' ▼') : ''}
                      </div>
                      <div style={{ fontWeight: 600, textAlign: 'right', cursor: 'pointer', userSelect: 'none' }} onClick={() => toggleSort('over')}>
                        Over % {radarSortBy === 'over' ? (radarSortDir === 'asc' ? '▲' : '▼') : ''}
                      </div>
                      <div style={{ fontWeight: 600, textAlign: 'right', cursor: 'pointer', userSelect: 'none' }} onClick={() => toggleSort('under')}>
                        Under % {radarSortBy === 'under' ? (radarSortDir === 'asc' ? '▲' : '▼') : ''}
                      </div>
                      <div style={{ fontWeight: 600, textAlign: 'center', cursor: 'pointer', userSelect: 'none' }} onClick={() => toggleSort('selected')}>
                        Show {radarSortBy === 'selected' ? (radarSortDir === 'asc' ? '▲' : '▼') : ''}
                      </div>
                      {sortedDevsForModal
                        .map((d: any) => (
                          <React.Fragment key={d._id}>
                            <div>{d.DeveloperName}</div>
                            <div style={{ textAlign: 'right', color: '#b71c1c' }}>{d._metrics.overPct.toFixed(1)}%</div>
                            <div style={{ textAlign: 'right', color: '#1b5e20' }}>{d._metrics.underPct.toFixed(1)}%</div>
                            <div style={{ textAlign: 'center' }}>
                              <input
                                type="checkbox"
                                checked={selectedRadarDeveloperIds.includes(d._id)}
                                onChange={(e) => {
                                  const checked = e.target.checked;
                                  setSelectedRadarDeveloperIds(prev => {
                                    const set = new Set(prev);
                                    if (checked) set.add(d._id); else set.delete(d._id);
                                    return Array.from(set);
                                  });
                                }}
                              />
                            </div>
                          </React.Fragment>
                        ))}
                    </div>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', padding: '12px 16px', borderTop: '1px solid #eee' }}>
                    <button onClick={() => setIsDevSelectModalOpen(false)} style={{ padding: '8px 14px', borderRadius: '8px', border: '1px solid #ddd', background: '#fff', cursor: 'pointer' }}>Close</button>
                  </div>
                </div>
              </div>, document.body)
            }
          </div>

          {/* Story Points Trend */}
          <div style={{
            background: 'rgba(255,255,255,0.6)',
            borderRadius: '12px',
            padding: isMobile ? '12px' : '16px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
          }}>
            <h3 style={{
              fontSize: '16px',
              marginBottom: '12px',
              color: '#3949ab'
            }}>Story Points Trend</h3>
            <div style={{ height: '200px' }}>
              <Line
                data={{
                  labels: teamData.recentIterations.map(iter => iter.IterationName),
                  datasets: [
                    {
                      label: 'Planned',
                      data: teamData.recentIterations.map(iter => iter.StoryPointsPlanned),
                      borderColor: 'rgb(54, 162, 235)',
                      backgroundColor: 'rgba(54, 162, 235, 0.2)',
                      fill: true
                    },
                    {
                      label: 'Completed',
                      data: teamData.recentIterations.map(iter => iter.StoryPointsCompleted),
                      borderColor: 'rgb(75, 192, 192)',
                      backgroundColor: 'rgba(75, 192, 192, 0.2)',
                      fill: true
                    }
                  ]
                }}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  scales: {
                    y: {
                      beginAtZero: true,
                      title: {
                        display: true,
                        text: 'Story Points'
                      }
                    }
                  }
                }}
              />
            </div>
          </div>

          {/* Completion Rate Trend */}
          <div style={{
            background: 'rgba(255,255,255,0.6)',
            borderRadius: '12px',
            padding: isMobile ? '12px' : '16px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
          }}>
            <h3 style={{
              fontSize: '16px',
              marginBottom: '12px',
              color: '#3949ab'
            }}>Completion Rate Trend</h3>
            <div style={{ height: '200px' }}>
              <Line
                data={{
                  labels: teamData.recentIterations.map(iter => iter.IterationName),
                  datasets: [{
                    label: 'Completion Rate',
                    data: teamData.recentIterations.map(iter => iter.CompletionPercentage),
                    borderColor: 'rgb(153, 102, 255)',
                    backgroundColor: 'rgba(153, 102, 255, 0.2)',
                    fill: true
                  }]
                }}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  scales: {
                    y: {
                      beginAtZero: true,
                      max: 100,
                      title: {
                        display: true,
                        text: 'Completion %'
                      }
                    }
                  }
                }}
              />
            </div>
          </div>


        </div>



        {/* Team Health Metrics */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
          gap: isMobile ? '16px' : '24px'
        }}>
          {/* Bug Resolution Trend */}
          <div style={{
            background: 'rgba(255,255,255,0.6)',
            borderRadius: '12px',
            padding: isMobile ? '12px' : '16px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
            border: '1px solid rgba(255,255,255,0.35)',
            backdropFilter: 'blur(10px)',
            WebkitBackdropFilter: 'blur(10px)'
          }}>
            <h3 style={{
              fontSize: '16px',
              marginBottom: '12px',
              color: '#3949ab'
            }}>Bug Resolution Trend</h3>
            <div style={{ height: '200px' }}>
              <Line
                data={{
                  labels: teamData.recentIterations.map(iter => iter.IterationName),
                  datasets: [
                    {
                      label: 'Created',
                      data: teamData.recentIterations.map(iter => iter.BugsCreated),
                      borderColor: 'rgb(255, 99, 132)',
                      backgroundColor: 'rgba(255, 99, 132, 0.2)',
                      fill: true
                    },
                    {
                      label: 'Resolved',
                      data: teamData.recentIterations.map(iter => iter.BugsResolved),
                      borderColor: 'rgb(75, 192, 192)',
                      backgroundColor: 'rgba(75, 192, 192, 0.2)',
                      fill: true
                    }
                  ]
                }}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  scales: {
                    y: {
                      beginAtZero: true,
                      title: {
                        display: true,
                        text: 'Number of Bugs'
                      }
                    }
                  }
                }}
              />
            </div>
          </div>

          {/* Build Quality Trend */}
          <div style={{
            background: 'rgba(255,255,255,0.6)',
            borderRadius: '12px',
            padding: isMobile ? '12px' : '16px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
            border: '1px solid rgba(255,255,255,0.35)',
            backdropFilter: 'blur(10px)',
            WebkitBackdropFilter: 'blur(10px)'
          }}>
            <h3 style={{
              fontSize: '16px',
              marginBottom: '12px',
              color: '#3949ab'
            }}>Build Quality Trend</h3>
            <div style={{ height: '200px' }}>
              <Line
                data={{
                  labels: teamData.recentIterations.map(iter => iter.IterationName),
                  datasets: [{
                    label: 'Build Success Rate',
                    data: teamData.recentIterations.map(iter => (100 - iter.BuildFailureRate)),
                    borderColor: 'rgb(255, 159, 64)',
                    backgroundColor: 'rgba(255, 159, 64, 0.2)',
                    fill: true
                  }]
                }}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  scales: {
                    y: {
                      beginAtZero: true,
                      max: 100,
                      title: {
                        display: true,
                        text: 'Success Rate %'
                      }
                    }
                  }
                }}
              />
            </div>
          </div>
        </div>
      </div>
    );
  }

  //individual developer performance overview
  if (!developer || !developer.IterationData || developer.IterationData.length === 0) {
    return (
      <div style={{
        background: 'white',
        borderRadius: '12px',
        padding: '24px',
        boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
        position: 'relative'
      }}>
        <button
          onClick={onClose}
          style={{
            position: 'absolute',
            top: '12px',
            right: '12px',
            background: 'none',
            border: 'none',
            fontSize: '20px',
            cursor: 'pointer'
          }}
        >
          ×
        </button>
        <h2>{developer?.DeveloperName || 'Developer'}</h2>
        <p>No iteration data available for this developer.</p>
      </div>
    );
  }

  // Sort iterations by date (newest first)
  const sortedIterations = [...(developer?.IterationData || [])].sort(
    (a, b) => new Date(b.StartDate).getTime() - new Date(a.StartDate).getTime()
  );

  // Prepare chart data
  const chartData = {
    labels: sortedIterations.map(iter => iter.IterationName),
    datasets: [
      {
        label: 'Story Points',
        data: sortedIterations.map(iter => iter.StoryPointsCompleted),
        borderColor: 'rgb(54, 162, 235)',
        backgroundColor: 'rgba(54, 162, 235, 0.2)',
        yAxisID: 'y'
      },
      {
        label: 'Completion Rate (%)',
        data: sortedIterations.map(iter => iter.CompletionRate),
        borderColor: 'rgb(75, 192, 192)',
        backgroundColor: 'rgba(75, 192, 192, 0.2)',
        yAxisID: 'y1'
      }
    ]
  };

  // Prepare radar chart data
  const latestIteration = sortedIterations[sortedIterations.length - 1];
  const radarData = {
    labels: ['Story Points', 'Completion Rate', 'Productivity', 'Work Items', 'Commits'],
    datasets: [{
      label: 'Latest Performance',
      data: [
        latestIteration.StoryPointsCompleted,
        latestIteration.CompletionRate,
        latestIteration.ProductivityScore,
        latestIteration.WorkItemsCompleted,
        latestIteration.CommitsCount
      ],
      backgroundColor: 'rgba(54, 162, 235, 0.2)',
      borderColor: 'rgb(54, 162, 235)',
      pointBackgroundColor: 'rgb(54, 162, 235)',
      pointBorderColor: '#fff',
      pointHoverBackgroundColor: '#fff',
      pointHoverBorderColor: 'rgb(54, 162, 235)',
      fill: true
    }]
  };

  return (
    <div style={glassCardBase}>
      {/* <button 
        onClick={onClose}
        style={{
          position: 'absolute',
          top: '12px',
          right: '12px',
          background: 'none',
          border: 'none',
          fontSize: '20px',
          cursor: 'pointer'
        }}
      >
        ×
      </button> */}

      <h2>{developer?.DeveloperName || 'Developer'} - Performance Details</h2>

      {/* AI Summary Section */}
      <div style={{ marginTop: '16px', marginBottom: isMobile ? '16px' : '24px' }}>
        <h3 style={{ borderBottom: '2px solid #3949ab', paddingBottom: '8px', marginBottom: '16px' }}>Sprint Summaries</h3>
        {isMobile ? (
          <MobileGlassSlider
            height={180}
            items={sortedIterations.map(iter => {
              const summaryKey = `${developer?.DeveloperId}-${iter.IterationId}`;
              const currentSummary = iterationAISummaries[summaryKey] || (iter.AiSummary ? iter.AiSummary.summary : '');
              const isGenerating = generatingAI[summaryKey];
              return (
                <div key={iter.IterationId}>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    marginBottom: 8
                  }}>
                    <div>
                      <h4 style={{ fontSize: 14, color: '#3949ab', margin: '0 0 4px 0' }}>{iter.IterationName}</h4>
                      <div style={{ fontSize: 12, color: '#666' }}>
                        {format(new Date(iter.StartDate), 'do MMMM yyyy')} - {format(new Date(iter.EndDate), 'do MMMM yyyy')}
                      </div>
                    </div>
                    <IconButton
                      onClick={() => handleGenerateIterationSummary(iter.IterationId, developer?.DeveloperId || '')}
                      disabled={isGenerating || !projectId}
                      title={currentSummary ? 'Regenerate Exec Insights' : 'Generate Exec Insights'}
                      icon="analytics"
                      isLoading={isGenerating}
                      size="medium"
                      variant="default"
                    />
                  </div>
                  <div style={{ position: 'relative', height: 310, overflow: 'hidden' }}>
                    <div
                      style={{ position: 'absolute', inset: 0, cursor: 'pointer', zIndex: 1 }}
                      onClick={() => {
                        handleOpenModal(
                          `Exec Insights - ${iter.IterationName}`,
                          currentSummary || 'Click the analysis button to generate exec insights for this iteration.',
                          'iteration',
                          iter.IterationId,
                          developer?.DeveloperId
                        );
                      }}
                      title="Click to view full content"
                    />
                    <div
                      style={{
                        display: '-webkit-box',
                        WebkitLineClamp: currentSummary ? 10 : 4,
                        WebkitBoxOrient: 'vertical' as any,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        position: 'relative',
                        zIndex: 0,
                        fontSize: currentSummary ? 12 : 14,
                        color: '#333',
                        lineHeight: 1.4
                      }}
                      dangerouslySetInnerHTML={{
                        __html: formatMarkdown(currentSummary ? `***click to view full summary***\n\n${currentSummary}` : 'Click the analysis button to generate exec insights for this iteration.')
                      }}
                    />
                  </div>
                </div>
              );
            })}
          />
        ) : (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: isMobile ? '12px' : '16px'
          }}>
            {sortedIterations.map(iter => {
              const summaryKey = `${developer?.DeveloperId}-${iter.IterationId}`;
              const currentSummary = iterationAISummaries[summaryKey] || (iter.AiSummary ? iter.AiSummary.summary : '');
              const isGenerating = generatingAI[summaryKey];

              return (
                <div key={iter.IterationId} style={{
                  background: 'rgba(255,255,255,0.65)',
                  borderRadius: '12px',
                  padding: isMobile ? '12px' : '16px',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                  border: '1px solid rgba(255,255,255,0.35)',
                  backdropFilter: 'blur(10px)',
                  WebkitBackdropFilter: 'blur(10px)'
                }}>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    marginBottom: '12px'
                  }}>
                    <div>
                      <h4 style={{
                        fontSize: '14px',
                        color: '#3949ab',
                        margin: '0 0 4px 0'
                      }}>
                        {iter.IterationName}
                      </h4>
                      <div style={{
                        fontSize: '12px',
                        color: '#666',
                        fontWeight: 'normal'
                      }}>
                        {format(new Date(iter.StartDate), 'do MMMM yyyy')} - {format(new Date(iter.EndDate), 'do MMMM yyyy')}
                      </div>
                    </div>

                    {/* Generate AI Summary Button */}
                    <IconButton
                      onClick={() => handleGenerateIterationSummary(iter.IterationId, developer?.DeveloperId || '')}
                      disabled={isGenerating || !projectId}
                      title={currentSummary ? "Regenerate AI Analysis" : "Generate AI Analysis"}
                      // icon={currentSummary ? 'refresh' : 'analytics'}
                      icon="analytics"
                      isLoading={isGenerating}
                      size="medium"
                      variant="default"
                    />
                  </div>

                  <div style={{
                    fontSize: currentSummary ? '6px' : '14px',
                    color: '#333',
                    lineHeight: '1.4',
                    height: '80px',
                    overflow: 'hidden',
                    position: 'relative'
                  }}
                  >
                    {/* Stable clickable overlay that doesn't change during streaming */}
                    <div
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        cursor: 'pointer',
                        zIndex: 1
                      }}
                      onClick={() => {
                        handleOpenModal(
                          `AI Analysis - ${iter.IterationName}`,
                          currentSummary || 'Click the analysis button to generate exec insights for this iteration.',
                          'iteration',
                          iter.IterationId,
                          developer?.DeveloperId
                        );
                      }}
                      title="Click to view full content"
                    />

                    {/* Content area that updates during streaming */}
                    <div
                      style={{
                        display: '-webkit-box',
                        WebkitLineClamp: currentSummary ? 10 : 4,
                        WebkitBoxOrient: 'vertical' as any,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        position: 'relative',
                        zIndex: 0
                      }}
                      dangerouslySetInnerHTML={{
                        __html: formatMarkdown(currentSummary || 'Click the analysis button to generate exec insights for this iteration.')
                      }}
                    />
                    {currentSummary && (
                      <div style={{
                        position: 'absolute',
                        bottom: '0',
                        right: '0',
                        background: 'linear-gradient(to right, transparent, rgba(248, 249, 250, 1) 50%)',
                        padding: '0 4px',
                        fontSize: '6px',
                        color: '#666',
                        pointerEvents: 'none',
                        zIndex: 0
                      }}>
                        ...
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Add CSS for spin animation */}
        <style>{`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}</style>
      </div>

      {/* Modal for full AI summary content */}
      {modalContent.isOpen && createPortal(
        <div className={isMobile ? 'modal-mobile' : 'modal'} onClick={handleCloseModal} style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999,
          margin: 0,
          padding: 0,
          isolation: 'isolate'
        }}>
          <div onClick={(e) => e.stopPropagation()} style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            width: isMobile ? '85%' : '90%',
            maxWidth: isMobile ? '600px' : '800px',
            maxHeight: isMobile ? '70vh' : '90vh',
            overflowY: 'auto',
            padding: isMobile ? '16px' : '24px',
            boxShadow: '0 8px 24px rgba(0,0,0,0.2)'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '20px',
              borderBottom: '1px solid #e0e0e0',
              paddingBottom: '16px'
            }}>
              <h2 style={{
                margin: 0,
                fontSize: '20px',
                fontWeight: 600,
                color: '#3949ab',
                marginRight: '8px'
              }}>
                {modalContent.title}
              </h2>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                {/* Generate AI Summary Button in Modal */}
                {modalContent.type === 'iteration' && modalContent.iterationId && modalContent.developerId && (
                  <IconButton
                    onClick={() => handleGenerateIterationSummary(modalContent.iterationId!, modalContent.developerId!)}
                    disabled={generatingAI[`${modalContent.developerId}-${modalContent.iterationId}`] || !projectId}
                    title={modalContent.content && !modalContent.content.includes('Click the analysis button') ? "Regenerate AI Analysis" : "Generate AI Analysis"}
                    icon={modalContent.content && !modalContent.content.includes('Click the analysis button') ? 'refresh' : 'analytics'}
                    isLoading={generatingAI[`${modalContent.developerId}-${modalContent.iterationId}`]}
                    variant="primary"
                    size="medium"
                  />
                )}

                {/* Generate Burndown Analysis Button in Modal */}
                {modalContent.type === 'burndown' && modalContent.developerId && (
                  <IconButton
                    onClick={() => handleGenerateBurndownAnalysis(modalContent.developerId!)}
                    disabled={generatingBurndownAnalysis || !projectId}
                    title={burndownAnalysis ? "Regenerate Burndown Analysis" : "Generate Burndown Analysis"}
                    icon={burndownAnalysis ? 'refresh' : 'analytics'}
                    isLoading={generatingBurndownAnalysis}
                    variant="primary"
                    size="medium"
                  />
                )}

                <button
                  onClick={handleCloseModal}
                  style={{
                    background: 'none',
                    border: 'none',
                    fontSize: '24px',
                    cursor: 'pointer',
                    color: '#666',
                    padding: '4px'
                  }}
                >
                  ×
                </button>
              </div>
            </div>

            <div style={{
              fontSize: '14px',
              lineHeight: '1.6',
              color: '#333'
            }}>
              <div dangerouslySetInnerHTML={{
                __html: formatMarkdown(
                  modalContent.type === 'iteration' && modalContent.iterationId && modalContent.developerId
                    ? (iterationAISummaries[`${modalContent.developerId}-${modalContent.iterationId}`] || modalContent.content)
                    : modalContent.type === 'burndown'
                      ? (burndownAnalysis || modalContent.content)
                      : modalContent.content
                )
              }} />
              {modalContent.type === 'iteration' && modalContent.iterationId && modalContent.developerId && generatingAI[`${modalContent.developerId}-${modalContent.iterationId}`] && (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  color: '#666',
                  fontStyle: 'italic',
                  marginTop: '16px',
                  fontSize: '14px'
                }}>
                  <span className="material-icons" style={{
                    fontSize: '16px',
                    marginRight: '8px',
                    animation: 'spin 1s linear infinite'
                  }}>
                    refresh
                  </span>
                  Generating AI analysis...
                </div>
              )}
              {modalContent.type === 'burndown' && generatingBurndownAnalysis && (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  color: '#666',
                  fontStyle: 'italic',
                  marginTop: '16px',
                  fontSize: '14px'
                }}>
                  <span className="material-icons" style={{
                    fontSize: '16px',
                    marginRight: '8px',
                    animation: 'spin 1s linear infinite'
                  }}>
                    refresh
                  </span>
                  Generating burndown analysis...
                </div>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Burndown Chart Section */}
      <div style={{ marginTop: '24px' }}>
        <h3 style={{ borderBottom: '2px solid #3949ab', paddingBottom: '8px' }}>Burndown Performance</h3>

        <DeveloperBurndownChart
          developer={developer as Developer}
          onOpenAnalysisModal={() => handleOpenModal(
            `Burndown Analysis - ${developer?.DeveloperName}`,
            burndownAnalysis || 'Click the analysis button to generate exec insights about burndown patterns across the last 3 iterations.',
            'burndown',
            undefined,
            developer?.DeveloperId
          )}
        />
      </div>

      {/* Quick Stats Section */}
      <div style={{
        marginTop: '16px',
        display: isMobile ? 'flex' : 'grid',
        flexDirection: isMobile ? 'row' : undefined,
        gridTemplateColumns: isMobile ? undefined : 'repeat(3, 1fr)',
        gap: isMobile ? '8px' : '12px',
        width: '100%'
      }}>
        <div style={{
          background: 'rgba(255,255,255,0.6)',
          borderRadius: '12px',
          padding: isMobile ? '12px' : '12px',
          textAlign: 'center',
          boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
          flex: isMobile ? '1' : undefined,
          minWidth: isMobile ? '0' : undefined
        }}>
          <div style={{ fontSize: '14px', color: '#666', marginBottom: '8px', minHeight: '36px' }}>Average Story Points</div>
          <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#3949ab' }}>
            {Math.round(sortedIterations.reduce((acc, iter) => acc + iter.StoryPointsCompleted, 0) / sortedIterations.length)}
          </div>
        </div>
        <div style={{
          background: 'rgba(255,255,255,0.6)',
          borderRadius: '12px',
          padding: isMobile ? '12px' : '12px',
          textAlign: 'center',
          boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
          flex: isMobile ? '1' : undefined,
          minWidth: isMobile ? '0' : undefined
        }}>
          <div style={{ fontSize: '14px', color: '#666', marginBottom: '8px', minHeight: '36px' }}>Average Completion</div>
          <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#3949ab' }}>
            {Math.round(sortedIterations.reduce((acc, iter) => acc + iter.CompletionRate, 0) / sortedIterations.length)}%
          </div>
        </div>
        <div style={{
          background: 'rgba(255,255,255,0.6)',
          borderRadius: '12px',
          padding: isMobile ? '12px' : '12px',
          textAlign: 'center',
          boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
          flex: isMobile ? '1' : undefined,
          minWidth: isMobile ? '0' : undefined
        }}>
          <div style={{ fontSize: '14px', color: '#666', marginBottom: '8px', minHeight: '36px' }}>Latest Productivity</div>
          <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#3949ab' }}>
            {Math.round(latestIteration.ProductivityScore)}%
          </div>
        </div>
      </div>

      {/* Performance Trends Section */}
      <div style={{
        marginTop: '24px',
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
        gap: isMobile ? '12px' : '16px'
      }}>
        {/* Story Points & Completion Rate Trend */}
        <div style={{
          background: 'rgba(255,255,255,0.65)',
          borderRadius: '12px',
          padding: isMobile ? '12px' : '16px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
          border: '1px solid rgba(255,255,255,0.35)',
          backdropFilter: 'blur(10px)',
          WebkitBackdropFilter: 'blur(10px)'
        }}>
          <h3 style={{
            fontSize: '16px',
            marginBottom: '12px',
            color: '#3949ab'
          }}>Performance Trends</h3>
          <div style={{ height: '200px' }}>
            <Line
              data={chartData}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                scales: {
                  y: {
                    beginAtZero: true,
                    position: 'left',
                    title: {
                      display: true,
                      text: 'Story Points'
                    }
                  },
                  y1: {
                    beginAtZero: true,
                    position: 'right',
                    title: {
                      display: true,
                      text: 'Completion Rate (%)'
                    },
                    max: 100,
                    grid: {
                      drawOnChartArea: false
                    }
                  }
                },
                plugins: {
                  legend: {
                    position: 'top' as const,
                  }
                }
              }}
            />
          </div>
        </div>

        {/* Estimate Accuracy Trend */}
        <div style={{
          background: 'rgba(255,255,255,0.65)',
          borderRadius: '12px',
          padding: isMobile ? '12px' : '16px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
          border: '1px solid rgba(255,255,255,0.35)',
          backdropFilter: 'blur(10px)',
          WebkitBackdropFilter: 'blur(10px)'
        }}>
          <h3 style={{
            fontSize: '16px',
            marginBottom: '12px',
            color: '#3949ab'
          }}>Estimate Accuracy Trend</h3>
          <div style={{ height: '200px' }}>
            <Line
              data={{
                labels: sortedIterations.map(iter => iter.IterationName),
                datasets: [
                  {
                    label: 'Over Estimates %',
                    data: sortedIterations.map(iter => {
                      const totalItems = (iter.ItemsOverEstimate || 0) + (iter.ItemsUnderEstimate || 0);
                      return totalItems > 0 ? ((iter.ItemsOverEstimate || 0) / totalItems) * 100 : 0;
                    }),
                    borderColor: 'rgb(244, 67, 54)',
                    backgroundColor: 'rgba(244, 67, 54, 0.2)',
                    fill: true
                  },
                  {
                    label: 'Under Estimates %',
                    data: sortedIterations.map(iter => {
                      const totalItems = (iter.ItemsOverEstimate || 0) + (iter.ItemsUnderEstimate || 0);
                      return totalItems > 0 ? ((iter.ItemsUnderEstimate || 0) / totalItems) * 100 : 0;
                    }),
                    borderColor: 'rgb(76, 175, 80)',
                    backgroundColor: 'rgba(76, 175, 80, 0.2)',
                    fill: true
                  }
                ]
              }}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                scales: {
                  y: {
                    beginAtZero: true,
                    max: 100,
                    title: {
                      display: true,
                      text: 'Percentage'
                    }
                  }
                },
                plugins: {
                  tooltip: {
                    callbacks: {
                      label: function (context) {
                        return `${context.dataset.label}: ${(context.raw as number).toFixed(1)}%`;
                      }
                    }
                  }
                }
              }}
            />
          </div>
        </div>
      </div>

      {/* Over-Estimated Items Gallery */}
      <div style={{
        marginTop: '24px',
        background: 'rgba(255,255,255,0.65)',
        borderRadius: '12px',
        padding: isMobile ? '12px' : '16px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
        border: '1px solid rgba(255,255,255,0.35)',
        backdropFilter: 'blur(10px)',
        WebkitBackdropFilter: 'blur(10px)'
      }}>
        <h3 style={{
          fontSize: '16px',
          marginBottom: '12px',
          color: '#3949ab'
        }}>Longest Over-Running Items</h3>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
          gap: isMobile ? '12px' : '16px'
        }}>
          {sortedIterations.map(iter => {
            let overEstimateItems: OverEstimateItem[] = [];
            try {
              if (typeof iter.TopOverEstimateItems === 'string') {
                overEstimateItems = JSON.parse(iter.TopOverEstimateItems);
              }
            } catch (e) {
              console.error('Error parsing TopOverEstimateItems:', e);
            }
            return Array.isArray(overEstimateItems) && overEstimateItems.length > 0 ? (
              <div key={iter.IterationId} style={{
                background: 'rgba(255,255,255,0.75)',
                borderRadius: '12px',
                padding: isMobile ? '10px' : '12px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                border: '1px solid rgba(255,255,255,0.35)',
                backdropFilter: 'blur(8px)',
                WebkitBackdropFilter: 'blur(8px)'
              }}>
                <h4 style={{
                  fontSize: '14px',
                  color: '#3949ab',
                  marginBottom: '8px'
                }}>{iter.IterationName}</h4>
                {overEstimateItems.map((item, idx) => (
                  <div key={idx} style={{
                    marginBottom: '8px',
                    padding: '8px',
                    background: 'rgba(255,255,255,0.65)',
                    borderRadius: '8px',
                    boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
                    border: '1px solid rgba(255,255,255,0.35)'
                  }}>
                    <div style={{ fontSize: '12px', color: '#666' }}>ID: {item.id}</div>
                    <div style={{ fontSize: '14px', fontWeight: 'bold', marginBottom: '4px' }}>{item.title}</div>
                    <div style={{ fontSize: '12px', color: '#666' }}>
                      Planned: {item.originalEstimate} days | Actual: {item.actualDuration} days
                    </div>
                    <div style={{
                      fontSize: '12px',
                      color: item.overEstimateRatio > 1 ? '#f44336' : '#4caf50'
                    }}>
                      {item.overEstimateRatio > 1 ? 'Over' : 'Under'} by {Math.abs(item.overEstimateRatio - 1).toFixed(1)}x
                    </div>
                  </div>
                ))}
              </div>
            ) : null
          })}
        </div>
      </div>

    </div>
  );
};

export default DeveloperDetailCard;
